import { useEffect, useState } from 'react';
import { IconPlus, IconLogout } from '@tabler/icons-react';
import { useIntakeStore } from '../../store/intake-store';
import { GabButton, GabBadge, GabDataTable } from '../../components/gab';
import { GabNativeSelect } from '../../components/gab/GabInput';
import { fmtPhone } from '../../lib/format-utils';
import { fmtDateTime } from '../../lib/date-format';
import type { Individual } from '../../types/intake';

const triageColor: Record<string, string> = { red: 'error', yellow: 'warning', green: 'success' };
const statusColor: Record<string, string> = { active: 'success', discharged: 'neutral', referred: 'primary', deceased: 'error' };

export default function Individuals() {
  const { individuals, total, loading, page, fetch, setPage, discharge } = useIntakeStore();
  const [filterTriage, setFilterTriage] = useState('');

  useEffect(() => { fetch(); }, []);
  useEffect(() => {
    const filters = filterTriage ? [{ field: 'triage', operator: 'eq' as const, value: filterTriage }] : undefined;
    fetch({ filters });
  }, [filterTriage]);

  const columns = [
    { key: 'lastName', label: 'Name', sortable: true, render: (r: Individual) => <span className="fw-semibold">{r.lastName}, {r.firstName}</span> },
    { key: 'triage', label: 'Triage', width: 80, render: (r: Individual) => <GabBadge variant={triageColor[r.triage] as any || 'neutral'}>{r.triage?.toUpperCase()}</GabBadge> },
    { key: 'status', label: 'Status', width: 100, render: (r: Individual) => <GabBadge variant={statusColor[r.status] as any || 'neutral'}>{r.status}</GabBadge> },
    { key: 'intakeMethod', label: 'Method', width: 110, render: (r: Individual) => r.intakeMethod || '—' },
    { key: 'phone', label: 'Phone', width: 130, render: (r: Individual) => fmtPhone(r.phone) },
    { key: 'address', label: 'Address', render: (r: Individual) => [r.address, r.city].filter(Boolean).join(', ') || '—' },
    { key: 'registeredAt', label: 'Registered', width: 140, render: (r: Individual) => fmtDateTime(r.registeredAt) },
    {
      key: 'actions', label: '', width: 110,
      render: (r: Individual) => r.status === 'active' ? (
        <GabButton size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); discharge(r.id); }}><IconLogout size={14} /> Discharge</GabButton>
      ) : null,
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <GabNativeSelect options={[{ value: '', label: 'All Triage' }, { value: 'var(--color-error)', label: 'Red' }, { value: '#C68700', label: 'Yellow' }, { value: 'var(--color-success)', label: 'Green' }]} value={filterTriage} onChange={e => setFilterTriage(e.target.value)} />
        <GabButton variant="primary" size="sm" leftIcon={<IconPlus size={14} />}>Register</GabButton>
      </div>
      <GabDataTable<Individual & Record<string, unknown>>
        columns={columns} data={individuals as (Individual & Record<string, unknown>)[]}
        total={total} page={page} pageSize={50} onPageChange={setPage}
        loading={loading} rowKey={r => r.id} striped emptyMessage="No individuals registered."
      />
    </div>
  );
}
